import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { initialPaymentFilters, paymentFiltersAtom } from '../store/atoms/paymentFiltersAtom';
import type { PaymentFilters, StudentPaymentSummary } from '../types/payment';

export function usePaymentList(personId?: number) {
  const [filters] = useAtom(paymentFiltersAtom);

  const filtered = useMemo(() => {
    let result: StudentPaymentSummary[] = [];

    if (personId) {
      result = result.filter(s => s.personId === personId);
    }
    if (filters.studentName) {
      const s = filters.studentName.toLowerCase();
      result = result.filter(st => st.personName.toLowerCase().includes(s));
    }
    if (filters.cpf) {
      result = result.filter(st => st.cpf.includes(filters.cpf));
    }
    if (filters.email) {
      const e = filters.email.toLowerCase();
      result = result.filter(st => st.email.toLowerCase().includes(e));
    }
    if (filters.nossoNumero) {
      result = result.filter(st =>
        st.plans.some(p => p.parcels.some(par => par.serialNumber.includes(filters.nossoNumero))),
      );
    }
    if (filters.paymentMethod !== 'all') {
      result = result
        .map(st => ({
          ...st,
          plans: st.plans.filter(p => p.paymentMethod === filters.paymentMethod),
        }))
        .filter(st => st.plans.length > 0);
    }
    if (filters.status !== 'all') {
      result = result
        .map(st => ({ ...st, plans: st.plans.filter(p => p.status === filters.status) }))
        .filter(st => st.plans.length > 0);
    }

    return result;
  }, [filters, personId]);

  return { data: filtered, isLoading: false, totalCount: filtered.length } as const;
}

export function usePaymentFilters() {
  const [filters, setFilters] = useAtom(paymentFiltersAtom);

  const updateFilters = (partial: Partial<PaymentFilters>) => {
    setFilters(prev => ({ ...prev, ...partial }));
  };

  const resetFilters = () => setFilters(initialPaymentFilters);

  return { filters, updateFilters, resetFilters } as const;
}
